import { useState, useCallback, useEffect, useMemo } from "react";
import { Sparkles, Send, X, Loader2, Lightbulb, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAITutor, type TutorHelpData } from "@/hooks/useAITutor";
import { LumiVoiceButton } from "@/components/lumi/LumiVoiceButton";
import { LumiAvatar } from "@/components/lumi/LumiAvatar";

interface Message {
  role: "user" | "ai";
  content: TutorHelpData | string;
}

interface Props {
  lessonId: string;
  lessonName: string;
  courseName: string;
  currentQuestion?: string;
  onClose: () => void;
}

const QUICK_QUESTIONS = [
  { label: "Explain this simply", prompt: "Explain this simply", useHint: false },
  { label: "Give me an example", prompt: "Give me an example", useHint: false },
  { label: "Define this term", prompt: "Define this term", useHint: true },
  { label: "Why is that answer wrong?", prompt: "Why is that answer wrong?", useHint: false },
  { label: "Help me before I answer", prompt: "Help me before I answer", useHint: true },
];

function toStoredMessage(role: string, content: string, payload: unknown): Message | null {
  if (role === "assistant" && payload && typeof payload === "object" && "answer" in (payload as Record<string, unknown>)) {
    return { role: "ai", content: payload as TutorHelpData };
  }

  if (role === "user") {
    return { role: "user", content };
  }

  if (role === "assistant") {
    return { role: "ai", content: content || "" };
  }

  return null;
}

export default function AITutorDrawer({ lessonId, lessonName, courseName, currentQuestion, onClose }: Props) {
  const { loading, getTutorHelp, getTutorHint } = useAITutor();
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [detailedMode, setDetailedMode] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      setHistoryLoading(true);
      const { data: session } = await supabase
        .from("ai_lesson_sessions")
        .select("id")
        .eq("lesson_id", lessonId)
        .eq("session_type", "lesson_tutor")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!session || cancelled) {
        setHistoryLoading(false);
        return;
      }

      const { data: rows } = await supabase
        .from("ai_lesson_session_messages")
        .select("role, content, structured_payload")
        .eq("session_id", session.id)
        .order("created_at", { ascending: true })
        .limit(20);

      if (cancelled) return;

      const restored = (rows || [])
        .map((row) => toStoredMessage(row.role, row.content, row.structured_payload))
        .filter(Boolean) as Message[];

      setMessages(restored);
      setHistoryLoading(false);
    };

    void loadHistory();

    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  const handleAsk = useCallback(async (q?: string, useHintModel?: boolean) => {
    const text = (q || question).trim();
    if (!text || loading) return;

    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setQuestion("");

    const fetcher = useHintModel ? getTutorHint : getTutorHelp;
    const result = await fetcher({
      question: text,
      lessonId,
      lessonName,
      courseName,
      currentQuestion,
      responseMode: detailedMode ? "detailed" : "short",
    });

    if (result.fallback || !result.data) {
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          content: {
            answer: "I'm having trouble connecting right now. Focus on the key money word in this lesson and what changes when you apply it.",
            analogy: "Think of the concept like a rule you can test in a small real-life situation first.",
            key_takeaway: "Start with the definition, then connect it to the choice or result in front of you.",
          },
        },
      ]);
      return;
    }

    setMessages((prev) => [...prev, { role: "ai", content: result.data }]);
  }, [question, loading, lessonId, lessonName, courseName, currentQuestion, detailedMode, getTutorHelp, getTutorHint]);

  const emptyState = useMemo(() => !historyLoading && messages.length === 0 && !loading, [historyLoading, messages.length, loading]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 flex max-h-[85vh] w-full flex-col rounded-t-2xl border border-border bg-card shadow-xl sm:max-w-md sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-border/50 p-4">
          <div className="flex items-center gap-2">
            <LumiAvatar size="sm" />
            <div>
              <h3 className="font-heading text-sm font-bold text-foreground">Ask Lumi</h3>
              <p className="text-[10px] text-muted-foreground">Your HeirWise learning companion</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDetailedMode((value) => !value)}
              className="h-7 px-2 text-[10px] text-muted-foreground"
            >
              {detailedMode ? "Detailed" : "Short"}
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {historyLoading && (
            <div className="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              Loading this lesson conversation…
            </div>
          )}

          {emptyState && (
            <div className="space-y-2 py-6 text-center">
              <Sparkles className="mx-auto h-8 w-8 text-primary/30" />
              <p className="text-xs text-muted-foreground">
                Ask about this question, a vocabulary word, or the concept behind the lesson.
              </p>
            </div>
          )}

          {messages.map((msg, index) => (
            <div key={index} className={msg.role === "user" ? "flex justify-end" : ""}>
              {msg.role === "user" ? (
                <div className="max-w-[85%] rounded-xl rounded-br-sm border border-primary/20 bg-primary/10 px-3 py-2">
                  <p className="text-sm text-foreground">{msg.content as string}</p>
                </div>
              ) : (
                <div className="max-w-[92%] space-y-2 animate-fade-in">
                  <div className="rounded-2xl border border-border/60 bg-card/70 p-3">
                    <div className="flex items-start gap-2">
                      <p className="flex-1 text-sm leading-relaxed text-foreground">
                        {typeof msg.content === "string" ? msg.content : msg.content.answer}
                      </p>
                      <LumiVoiceButton
                        text={typeof msg.content === "string" ? msg.content : msg.content.answer}
                        contextId={lessonId}
                        label="Hear Lumi read this response"
                      />
                    </div>
                  </div>

                  {typeof msg.content !== "string" && detailedMode && (
                    <>
                      <div className="rounded-xl border border-border/50 bg-background/40 p-2.5">
                        <div className="mb-1 flex items-center gap-1.5">
                          <Lightbulb className="h-3 w-3 text-primary" />
                          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">Example</span>
                        </div>
                        <p className="text-xs leading-relaxed text-muted-foreground">{msg.content.analogy}</p>
                      </div>
                      <div className="rounded-xl border border-border/50 bg-background/40 p-2.5">
                        <div className="mb-1 flex items-center gap-1.5">
                          <BookOpen className="h-3 w-3 text-primary" />
                          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">Key takeaway</span>
                        </div>
                        <p className="text-xs leading-relaxed text-muted-foreground">{msg.content.key_takeaway}</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex items-center gap-2 py-3">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-xs text-muted-foreground">HeirWise AI is thinking…</span>
            </div>
          )}
        </div>

        {!loading && (
          <div className="flex flex-wrap gap-1.5 px-4 pb-2">
            {QUICK_QUESTIONS.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => handleAsk(item.prompt, item.useHint)}
                className="rounded-full border border-border bg-muted/30 px-2.5 py-1.5 text-[10px] text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        <div className="border-t border-border/50 p-4 pt-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAsk()}
              placeholder="Ask about this lesson…"
              className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
            <Button
              size="icon"
              onClick={() => handleAsk()}
              disabled={!question.trim() || loading}
              className="h-9 w-9 shrink-0"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
